import React, { useState, useEffect } from 'react';
import {
  canIssueReports,
  collectGarbage,
  goTo,
  setHelpPage,
  setWindowTitle,
} from '../../configTdm';
import moment from 'moment';
import _ from 'lodash';
import IssuesReportingForm from './IssuesReportingForm';
import IssuesReportingCosts from './IssuesReportingCosts';
import IssuesReportResults from './IssuesReportResults';
import ReportDownload from '../reportDownload';
import { getProperty } from "../../fsConfig";
import { issuesReportingService } from '../reportingService';
import FullPageLoadingSpinner from '../../pages/components/fullPageLoadingSpinner';

interface Report {
  summary: any;
  issueResolutionsAndTypes: any;
  travelersContacted: any;
  issuesByRoute: any;
  issuesByHour: any;
  issuesByDay: any;
  issuesByCarrier: any;
  issuesByTraveler: any;
}

interface Csv {
  data: any;
  name: string;
  startDate: string;
  endDate: string;
  duration: string;
}

const IssueReport: React.FC = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [csv, setCsv] = useState<Csv | null>(null);
  const [reportDateRange, setReportDateRange] = useState<string | null>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Set initial loading state to true
  const { accountName } = getProperty('session', {});

  useEffect(() => {
    if (!canIssueReports()) {
      goTo('/home');
    }

    setWindowTitle('Issues Reporting');
    setHelpPage('helpIssuesReport');

    // Simulate initial loading delay
    const initialLoadTimer = setTimeout(() => {
      setLoading(false);
    }, 1000); // Adjust the delay as needed

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      clearTimeout(initialLoadTimer);
      setReport(null);
      setCsv(null);
      setReportDateRange(null);
      collectGarbage();
    };
  }, []);

  const haveReport = (): boolean => !_.isEmpty(report);

  const costsUpdated = (costs: any) => {
    if (!report) return;
    const updatedReport = { ...report };
    updatedReport.summary.minutesSaved.laborRate = costs.laborRate;
    updatedReport.summary.overnightsAvoided.hotelStay = costs.hotelStay;
    updatedReport.summary.overnightsAvoided.perDiem = costs.perDiem;
    updatedReport.summary.minutesSaved.costDeflected = Math.abs(
      updatedReport.summary.minutesSaved.inHrs * parseInt(costs.laborRate)
    ).toFixed(2);
    updatedReport.summary.overnightsAvoided.costDeflected =
      updatedReport.summary.overnightsAvoided.count *
      (parseInt(costs.hotelStay) + parseInt(costs.perDiem));
    setReport(updatedReport);
  };

  const processResults = (results: any) => {
    const startDate = moment(results.request.resolveByMin).format('MMMM DD, YYYY');
    const endDate = moment(results.request.resolveByMax).format('MMMM DD, YYYY');
    setReportDateRange(startDate === endDate ? startDate : `${startDate} through ${endDate}`);

    setReport({
      summary: results.summary,
      issueResolutionsAndTypes: results.summary.issueResolutionsAndTypes,
      travelersContacted: results.summary.travelers,
      issuesByRoute: results.byRoute,
      issuesByHour: results.byDatesTimes.byHourOfDay,
      issuesByDay: results.byDatesTimes.byDayOfWeek,
      issuesByCarrier: results.byCarriers,
      issuesByTraveler: results.byTraveler,    
    });

    setCsv({
      data: results.csv,
      name: 'disruptionReport',
      startDate: results.request.resolveByMin,
      endDate: results.request.resolveByMax,
      duration: results.request.duration,
    });
  };

  const reset = () => {
    setReport(null);
    setCsv(null);
  };

  const refreshContent = () => {
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => {}, 100));
  };

  const fetchReport = async (searchRequest: any) => {
    setSearching(true);
    setLoading(true); 
    setReport(null);
    try {
      const response = await issuesReportingService.fetch(searchRequest);
      if (response.status !== 200) {
        throw new Error('Failed to fetch data');
      }
      setSearching(false);
      const results = response.data;
      results.request = searchRequest;
      processResults(results);
    } catch (error) {
      console.error(error);
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      {loading && <FullPageLoadingSpinner />} {/* Conditionally render the spinner */}
      <div className="row search-title">
        <div className="col-md-12">
          ISSUES REPORTING
        </div>
      </div>
      <IssuesReportingForm
        onReset={reset}
        fetchReport={fetchReport}
        searching={searching}
      />
      {haveReport() && (
        <div id="report-results-set" className="row report-results">
          <IssuesReportingCosts onChange={costsUpdated} />
          <div className="col-md-12 results-header-container">
            <div className="col-md-12 results-header">
              <div className="col-md-6 main-title">
                TRIP ASSIST ISSUE REPORT
              </div>
              <div className="col-md-6 sub-title">
                {csv && <ReportDownload csv={csv} />}
                <br />Report for {reportDateRange}
              </div>
            </div>
            <div className="col-md-12">
              <div className="col-md-6 sub-main-title">
                Trip Assist Issue Report by {accountName}
              </div>
              <div className="col-md-6 sub-title">
                Powered by Cirium
              </div>
            </div>
          </div>
          {report && <IssuesReportResults report={report} onChartView={refreshContent} />}
        </div>
      )}
    </div>
  );
};

export default IssueReport;